import { TypeIcon } from "../components/common/TypeIcon";
import { usePageBar } from "../contexts/PageBarContext";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTrips } from "../hooks/useTrips";
import { useEntities } from "../hooks/useEntities";
import { useUnlinkedInteractions, type UnlinkedInteraction } from "../hooks/useUnlinkedInteractions";
import { EntityPicker } from "../components/entities/EntityPicker";
import {
  groupByName,
  relatedGroups,
  prepareCandidates,
  rankPrepared,
  MATCH_STRONG,
  type NameGroup,
  type ScoredMatch,
} from "../utils/entityMatching";
import {
  createEntityFromActivity,
  linkInteractionsToEntity,
  setActivitiesNoPlace,
  unlinkInteraction,
} from "../utils/entityLinking";
import { getActivityTimeRange, entityTypeLabel } from "../utils/tripDisplay";
import { ENTITY_TYPES, getInteractionEntityType, type Entity, type EntityType } from "../types";
import "./LinkActivities.css";

type EntityWithId = Entity & { id: string };

/** How many groups render at once — see the note on `visibleCount`. */
const PAGE_SIZE = 20;

type Scope = "open" | "repeats" | "matched" | "dismissed";

const SCOPES: { id: Scope; label: string }[] = [
  { id: "open", label: "Needs a place" },
  { id: "repeats", label: "Repeats" },
  { id: "matched", label: "Has a match" },
  { id: "dismissed", label: "Not a place" },
];

function confidenceLabel(score: number): string {
  if (score >= 0.98) return "Same name";
  if (score >= MATCH_STRONG) return "Very likely";
  if (score >= 0.7) return "Likely";
  return "Possible";
}

/** The type an unlinked activity claims for itself, if it was ever given one. */
function activityType(i: UnlinkedInteraction): EntityType | undefined {
  return getInteractionEntityType(null, i.customResponses);
}

/**
 * The backlog of activities logged by name only, one row per name.
 *
 * Every activity here needs one of three answers, and the page is built so all
 * three cost the same: it's *this* place in the catalog, it's a place we don't
 * have yet, or it isn't a place at all. That third answer matters more than it
 * sounds — a third of this backlog is "Breakfast", "Coffee", "Safety Drill",
 * the flight home. Without a way to say no, they sit at the top of the list
 * forever and bury the entries that actually need work.
 *
 * Grouping is by name, exactly (after normalizing case, punctuation, accents
 * and plurals) — never by resemblance. Names that merely *look* alike are
 * offered next to the group as something to pull in, because a merge you can
 * see is worth far more than one that happened to you. See `groupByName`.
 */
export function LinkActivities() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const canManageEntities = !!userProfile?.roles?.includes("manage_entities");
  usePageBar({ title: "Link activities", parent: { to: "/entities", label: "Entities" } });

  const tripFilter = searchParams.get("tripId");
  const search = searchParams.get("q") ?? "";
  const scope = (searchParams.get("scope") as Scope | null) ?? "open";

  const { trips, loading: tripsLoading } = useTrips();
  const tripIds = useMemo(
    () => (tripFilter ? [tripFilter] : trips.map((t) => t.id)),
    [tripFilter, trips],
  );
  const { interactions, loading: interactionsLoading, incomplete } = useUnlinkedInteractions(tripIds);
  const { entities, loading: entitiesLoading } = useEntities();

  const tripNames = useMemo(
    () => Object.fromEntries(trips.map((t) => [t.id, t.name])),
    [trips],
  );

  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [pulledIn, setPulledIn] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [picking, setPicking] = useState<string | null>(null);
  const [creating, setCreating] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [lastAction, setLastAction] = useState<
    { ids: string[]; kind: "linked"; entityName: string } | { ids: string[]; kind: "dismissed" } | null
  >(null);

  // Answered (dismissed) and open activities are two different piles of work;
  // the counts and the group list both follow whichever one is on screen.
  const open = useMemo(() => interactions.filter((i) => !i.noPlace), [interactions]);
  const dismissed = useMemo(() => interactions.filter((i) => i.noPlace), [interactions]);
  const showingDismissed = scope === "dismissed";

  const allGroups = useMemo(
    () => groupByName(showingDismissed ? dismissed : open, (i) => i.name ?? ""),
    [showingDismissed, dismissed, open],
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return allGroups.filter((g) => {
      if (scope === "repeats" && g.items.length < 2) return false;
      if (needle && !g.variants.some((v) => v.toLowerCase().includes(needle))) return false;
      return true;
    });
  }, [allGroups, search, scope]);

  // Ranking every group against the whole catalog up front is wasted work on a
  // page you scroll through a screen at a time, so only the groups actually on
  // screen are matched. The "has a match" filter is the exception — it has to
  // know about every group to filter by it.
  const preparedEntities = useMemo(
    () => prepareCandidates(entities, (e) => e.displayName),
    [entities],
  );
  const matchLimit = scope === "matched" ? filtered.length : visibleCount;
  const suggestions = useMemo(() => {
    const out = new Map<string, ScoredMatch<EntityWithId>[]>();
    for (const group of filtered.slice(0, matchLimit)) {
      out.set(group.key, rankPrepared(group.label, preparedEntities, { limit: 3 }));
    }
    return out;
  }, [filtered, matchLimit, preparedEntities]);

  const groups = useMemo(
    () =>
      scope === "matched"
        ? filtered.filter((g) => (suggestions.get(g.key)?.length ?? 0) > 0)
        : filtered,
    [filtered, scope, suggestions],
  );

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
    setVisibleCount(PAGE_SIZE);
  };

  const toggle = (set: Set<string>, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };

  /**
   * Which activities an action applies to: the group's own, minus anything
   * unticked, plus any related group the user pulled in.
   */
  const targetIds = (group: NameGroup<UnlinkedInteraction>) => {
    const items = [...group.items];
    for (const related of relatedGroups(group, allGroups)) {
      if (pulledIn.has(`${group.key}|${related.item.key}`)) items.push(...related.item.items);
    }
    return items.map((i) => i.id).filter((id) => !excluded.has(id));
  };

  const applyLink = (group: NameGroup<UnlinkedInteraction>, entityId: string, entityName: string) => {
    const ids = targetIds(group);
    if (ids.length === 0) return;
    linkInteractionsToEntity(ids, entityId);
    setLastAction({ ids, kind: "linked", entityName });
    setPicking(null);
    setCreating(null);
  };

  const applyNoPlace = (group: NameGroup<UnlinkedInteraction>) => {
    const ids = targetIds(group);
    if (ids.length === 0) return;
    setActivitiesNoPlace(ids, true);
    setLastAction({ ids, kind: "dismissed" });
  };

  const undoLast = () => {
    if (!lastAction) return;
    // Not `forEach(unlinkInteraction)` — that hands the callback the index
    // and the array too, which the next argument this function grows would
    // silently pick up.
    if (lastAction.kind === "linked") lastAction.ids.forEach((id) => unlinkInteraction(id));
    else setActivitiesNoPlace(lastAction.ids, false);
    setLastAction(null);
  };

  const loading = tripsLoading || interactionsLoading || entitiesLoading;
  const tripCount = new Set(open.map((i) => i.tripId)).size;

  return (
    <div className="page link-activities">
      <div className="section-header">
        <h1>Link Activities</h1>
      </div>

      <p className="text-muted la-intro">
        Activities logged by name only, one row per name. Link a row to a place and
        every activity in it joins that place — across trips. Anything that isn't a
        place (breakfast, the safety drill) can be set aside instead.
      </p>

      <div className="la-controls">
        <select
          className="form-input la-trip-select"
          value={tripFilter ?? ""}
          onChange={(e) => setParam("tripId", e.target.value || null)}
        >
          <option value="">All trips</option>
          {trips.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <input
          type="search"
          className="form-input la-search"
          placeholder="Search names..."
          value={search}
          onChange={(e) => setParam("q", e.target.value || null)}
        />
      </div>

      <div className="la-scopes">
        {SCOPES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`la-scope${scope === s.id ? " on" : ""}`}
            onClick={() => setParam("scope", s.id === "open" ? null : s.id)}
          >
            {s.label}
            {s.id === "dismissed" && dismissed.length > 0 ? ` (${dismissed.length})` : ""}
          </button>
        ))}
      </div>

      {lastAction && (
        <div className="la-undo">
          <span>
            {lastAction.kind === "linked" ? (
              <>
                Linked {lastAction.ids.length}{" "}
                {lastAction.ids.length === 1 ? "activity" : "activities"} to{" "}
                <strong>{lastAction.entityName}</strong>.
              </>
            ) : (
              <>
                Set aside {lastAction.ids.length}{" "}
                {lastAction.ids.length === 1 ? "activity" : "activities"} as not a place.
              </>
            )}
          </span>
          <button type="button" className="btn-link" onClick={undoLast}>Undo</button>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading activities...</div>
      ) : showingDismissed && dismissed.length === 0 ? (
        <p className="text-muted">
          Nothing set aside yet. Use “Not a place” on a row that isn't a venue —
          breakfast, a pool afternoon, the flight home.
        </p>
      ) : !showingDismissed && open.length === 0 ? (
        <p className="text-muted">
          {tripFilter
            ? "Every activity on this trip has been dealt with."
            : "Every activity is either linked to a place or set aside. Nothing left."}
        </p>
      ) : (
        <>
          <p className="la-summary">
            {showingDismissed ? (
              <>
                <strong>{dismissed.length}</strong> set aside as not a place
              </>
            ) : (
              <>
                <strong>{open.length}</strong> {open.length === 1 ? "activity" : "activities"} still
                need a place
                {!tripFilter && tripCount > 1 ? ` across ${tripCount} trips` : ""} ·{" "}
                <strong>{allGroups.length}</strong> distinct{" "}
                {allGroups.length === 1 ? "name" : "names"}
              </>
            )}
          </p>
          {incomplete && (
            <p className="text-muted" role="status">
              Some trips couldn&apos;t be loaded, so this list may be missing activities. Reload to try again.
            </p>
          )}

          {groups.length === 0 ? (
            <p className="text-muted">No names match that filter.</p>
          ) : (
            <ul className="la-groups">
              {groups.slice(0, visibleCount).map((group) => (
                <LinkGroup
                  key={group.key}
                  group={group}
                  related={relatedGroups(group, allGroups)}
                  pulledIn={pulledIn}
                  matches={suggestions.get(group.key) ?? []}
                  tripNames={tripNames}
                  excluded={excluded}
                  dismissedView={showingDismissed}
                  expanded={expanded.has(group.key)}
                  picking={picking === group.key}
                  creating={creating === group.key}
                  canManageEntities={canManageEntities}
                  targetCount={targetIds(group).length}
                  onToggleExpanded={() => setExpanded((prev) => toggle(prev, group.key))}
                  onTogglePicking={() => {
                    setCreating(null);
                    setPicking((prev) => (prev === group.key ? null : group.key));
                  }}
                  onToggleCreating={() => {
                    setPicking(null);
                    setCreating((prev) => (prev === group.key ? null : group.key));
                  }}
                  onToggleItem={(id) => setExcluded((prev) => toggle(prev, id))}
                  onTogglePulledIn={(relatedKey) =>
                    setPulledIn((prev) => toggle(prev, `${group.key}|${relatedKey}`))
                  }
                  onLink={(entityId, entityName) => applyLink(group, entityId, entityName)}
                  onNoPlace={() => applyNoPlace(group)}
                  onRestore={() => {
                    const ids = group.items.map((i) => i.id);
                    setActivitiesNoPlace(ids, false);
                    setLastAction(null);
                  }}
                />
              ))}
            </ul>
          )}

          {groups.length > visibleCount && (
            <button
              type="button"
              className="btn btn-secondary btn-block la-more"
              onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
            >
              Show more ({groups.length - visibleCount} left)
            </button>
          )}
        </>
      )}
    </div>
  );
}

interface LinkGroupProps {
  group: NameGroup<UnlinkedInteraction>;
  related: ScoredMatch<NameGroup<UnlinkedInteraction>>[];
  pulledIn: Set<string>;
  matches: ScoredMatch<EntityWithId>[];
  tripNames: Record<string, string>;
  excluded: Set<string>;
  dismissedView: boolean;
  expanded: boolean;
  picking: boolean;
  creating: boolean;
  canManageEntities: boolean;
  targetCount: number;
  onToggleExpanded: () => void;
  onTogglePicking: () => void;
  onToggleCreating: () => void;
  onToggleItem: (id: string) => void;
  onTogglePulledIn: (relatedKey: string) => void;
  onLink: (entityId: string, entityName: string) => void;
  onNoPlace: () => void;
  onRestore: () => void;
}

function LinkGroup({
  group,
  related,
  pulledIn,
  matches,
  tripNames,
  excluded,
  dismissedView,
  expanded,
  picking,
  creating,
  canManageEntities,
  targetCount,
  onToggleExpanded,
  onTogglePicking,
  onToggleCreating,
  onToggleItem,
  onTogglePulledIn,
  onLink,
  onNoPlace,
  onRestore,
}: LinkGroupProps) {
  const type = group.items.map(activityType).find(Boolean);
  const trips = [...new Set(group.items.map((i) => tripNames[i.tripId]).filter(Boolean))];

  return (
    <li className="la-group">
      <div className="la-group-head">
        <span className="la-group-icon"><TypeIcon type={type} /></span>
        <div className="la-group-title">
          <h3>{group.label}</h3>
          <div className="la-group-meta">
            <span>{group.items.length} logged</span>
            {group.variants.length > 1 && <span>also “{group.variants.slice(1).join("”, “")}”</span>}
            {type && <span>{entityTypeLabel(type)}</span>}
            {trips.length > 0 && (
              <span>
                {trips.slice(0, 2).join(", ")}
                {trips.length > 2 ? ` +${trips.length - 2}` : ""}
              </span>
            )}
          </div>
        </div>
      </div>

      {dismissedView ? (
        <div className="la-group-actions">
          <button type="button" className="btn-link" onClick={onToggleExpanded}>
            {expanded ? "Hide" : "Show"} {group.items.length === 1 ? "activity" : `${group.items.length} activities`}
          </button>
          <button type="button" className="btn-link" onClick={onRestore}>
            Put back in the queue
          </button>
        </div>
      ) : (
        <>
          {/* Names that look like this one. Never merged automatically — tick
              one and its activities come along with whatever you choose. */}
          {related.length > 0 && (
            <ul className="la-related">
              {related.map((r) => (
                <li key={r.item.key}>
                  <label>
                    <input
                      type="checkbox"
                      checked={pulledIn.has(`${group.key}|${r.item.key}`)}
                      onChange={() => onTogglePulledIn(r.item.key)}
                    />
                    <span>
                      also “{r.item.label}” ({r.item.items.length})
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}

          {matches.length > 0 ? (
            <ul className="la-matches">
              {matches.map((match) => (
                <li key={match.item.id}>
                  <button
                    type="button"
                    className="la-match"
                    disabled={targetCount === 0}
                    onClick={() => onLink(match.item.id, match.item.displayName)}
                  >
                    <span className="la-match-name">
                      <TypeIcon type={match.item.type} name={match.item.displayName} /> {match.item.displayName}
                    </span>
                    <span className="la-match-meta">
                      <span className="la-match-type">{entityTypeLabel(match.item.type)}</span>
                      <span className={`la-confidence${match.score >= MATCH_STRONG ? " strong" : ""}`}>
                        {confidenceLabel(match.score)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="la-nomatch text-muted">No place in the catalog looks like this.</p>
          )}

          <div className="la-group-actions">
            <button type="button" className="btn-link" onClick={onToggleExpanded}>
              {expanded ? "Hide" : "Show"} {group.items.length === 1 ? "activity" : `${group.items.length} activities`}
            </button>
            <button type="button" className="btn-link" onClick={onTogglePicking}>
              {picking ? "Cancel" : "Pick a place"}
            </button>
            {canManageEntities && (
              <button type="button" className="btn-link" onClick={onToggleCreating}>
                {creating ? "Cancel" : "Create place"}
              </button>
            )}
            <button
              type="button"
              className="btn-link la-dismiss"
              onClick={onNoPlace}
              disabled={targetCount === 0}
            >
              Not a place
            </button>
          </div>
        </>
      )}

      {picking && (
        <div className="la-picker">
          <EntityPicker
            types={[...ENTITY_TYPES]}
            value={null}
            placeholder="Search the catalog..."
            onChange={(entityId, entity) => {
              if (entityId && entity) onLink(entityId, entity.displayName);
            }}
          />
        </div>
      )}

      {creating && (
        <CreatePlaceForm
          defaultName={group.label}
          defaultType={type ?? "ATTRACTION"}
          count={targetCount}
          onCancel={onToggleCreating}
          onCreate={(entityId, entityName) => onLink(entityId, entityName)}
        />
      )}

      {expanded && (
        <ul className="la-items">
          {group.items.map((item) => {
            // Only the activity's own recorded time. A backfilled note has
            // none, and `createdAt` would date it to the day it was typed up.
            const when = getActivityTimeRange(item.events).start;
            return (
              <li key={item.id} className={excluded.has(item.id) ? "off" : ""}>
                <label className="la-item-check">
                  <input
                    type="checkbox"
                    checked={!excluded.has(item.id)}
                    onChange={() => onToggleItem(item.id)}
                    disabled={dismissedView}
                  />
                  <span className="la-item-name">{item.name || "Unnamed activity"}</span>
                </label>
                <span className="la-item-meta">
                  {tripNames[item.tripId] ?? "Unknown trip"}
                  {when
                    ? ` · ${when.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
                    : ""}
                </span>
                <Link to={`/trips/${item.tripId}/interactions/${item.id}`} className="la-item-open">
                  Open
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {!dismissedView && targetCount === 0 && (
        <p className="la-nomatch text-muted">Nothing selected in this group.</p>
      )}
      {!dismissedView && targetCount > 0 && targetCount !== group.items.length && (
        <p className="la-nomatch text-muted">
          Applies to {targetCount} of {group.items.length}
          {related.some((r) => pulledIn.has(`${group.key}|${r.item.key}`)) ? " (plus the names you ticked)" : ""}.
        </p>
      )}
    </li>
  );
}

interface CreatePlaceFormProps {
  defaultName: string;
  defaultType: EntityType;
  count: number;
  onCancel: () => void;
  onCreate: (entityId: string, entityName: string) => void;
}

/**
 * Turn what the group recorded into a catalog entity. Kept to the three fields
 * that decide what the place *is* — the rest of the entity form (image, wiki
 * link, event options) is a step you take on the entity itself afterwards.
 */
function CreatePlaceForm({ defaultName, defaultType, count, onCancel, onCreate }: CreatePlaceFormProps) {
  const [displayName, setDisplayName] = useState(defaultName);
  const [type, setType] = useState<EntityType>(defaultType);
  const [parentId, setParentId] = useState<string | null>(null);

  const submit = () => {
    const name = displayName.trim();
    if (!name) return;
    onCreate(createEntityFromActivity({ displayName: name, type, parentId }), name);
  };

  return (
    <div className="la-create">
      <label className="form-field">
        <span className="form-label">Name</span>
        <input
          type="text"
          className="form-input"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
      </label>
      <label className="form-field">
        <span className="form-label">Type</span>
        <select
          className="form-input"
          value={type}
          onChange={(e) => setType(e.target.value as EntityType)}
        >
          {ENTITY_TYPES.map((t) => (
            <option key={t} value={t}>{entityTypeLabel(t)}</option>
          ))}
        </select>
      </label>
      <div className="form-field">
        <span className="form-label">Inside (optional)</span>
        <EntityPicker
          types={["DESTINATION", "PARK", "EVENT", "HOTEL", "SHIP", "AIRPORT", "LOCATION"]}
          value={parentId}
          onChange={(id) => setParentId(id)}
          placeholder="Park, hotel, ship..."
        />
      </div>
      <div className="form-actions">
        <button
          type="button"
          className="btn btn-small"
          onClick={submit}
          disabled={!displayName.trim() || count === 0}
        >
          Create &amp; link {count}
        </button>
        <button type="button" className="btn btn-secondary btn-small" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
